import { useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@nexus/ui";
import type { OverviewFilters, OverviewKpiSeries } from "@nexus/contracts";
import { useTranslation } from "react-i18next";
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  TOPIC_DETAIL_DRAWER_WIDTH,
  type TopicDetailContext,
} from "../../lib/topicDetailContext.js";
import { shouldKeepTopicDetailDrawerOpen } from "./shouldKeepTopicDetailDrawerOpen.js";
import { TopicDetailInfoTab } from "./TopicDetailInfoTab.js";
import { TopicDetailExamplesTab } from "./TopicDetailExamplesTab.js";
import { TopicDetailFatigueTab } from "./TopicDetailFatigueTab.js";
export type TopicDetailTab = "info" | "examples" | "fatigue";

export interface TopicDetailDrawerProps {
  tenantId: string;
  context: TopicDetailContext | null;
  open: boolean;
  activeTab: TopicDetailTab;
  onOpenChange: (open: boolean) => void;
  onActiveTabChange: (tab: TopicDetailTab) => void;
  kpiSeries: OverviewKpiSeries[];
  overviewFilters: OverviewFilters;
  globalFilters: MyBrandFiltersInput;
}

export function TopicDetailDrawer({
  tenantId,
  context,
  open,
  activeTab,
  onOpenChange,
  onActiveTabChange,
  kpiSeries,
  overviewFilters,
  globalFilters,
}: TopicDetailDrawerProps) {
  const { t } = useTranslation();
  const examplesScrollRef = useRef<HTMLDivElement>(null);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeIcon="chevron-right"
        className={`flex w-full flex-col overflow-hidden bg-card p-0 ${TOPIC_DETAIL_DRAWER_WIDTH}`}
        onPointerDownOutside={(e) => {
          if (shouldKeepTopicDetailDrawerOpen(e.target)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (shouldKeepTopicDetailDrawerOpen(e.target)) e.preventDefault();
        }}
      >
        <SheetHeader className="border-b px-6 pb-4 pt-6">
          <div className="pl-10">
            <SheetTitle className="text-base font-medium">
              {t("overview.topicDetail.title", { defaultValue: "Topic Detail" })}
            </SheetTitle>
          </div>
        </SheetHeader>

        {!context ? (
          <p className="px-6 py-5 text-sm text-muted-foreground">
            {t("overview.topicDetail.noContext", { defaultValue: "Topic context is unavailable." })}
          </p>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(v) => onActiveTabChange(v as TopicDetailTab)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="border-b px-6">
              <TabsList className="h-10 w-full justify-start rounded-none border-0 bg-transparent p-0">
                <TabsTrigger value="info" className="rounded-none">
                  {t("overview.topicDetail.tabInfo", { defaultValue: "Info" })}
                </TabsTrigger>
                <TabsTrigger value="examples" className="rounded-none">
                  {t("overview.topicDetail.tabExamples", { defaultValue: "Examples" })}
                </TabsTrigger>
                <TabsTrigger value="fatigue" className="rounded-none">
                  {t("overview.topicDetail.tabFatigue", { defaultValue: "Fatigue" })}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="info" className="mt-0 min-h-0 flex-1 overflow-y-auto">
              <TopicDetailInfoTab context={context} kpiSeries={kpiSeries} />
            </TabsContent>

            <TabsContent
              value="examples"
              ref={examplesScrollRef}
              className="mt-0 min-h-0 flex-1 overflow-y-auto"
            >
              {activeTab === "examples" && (
                <TopicDetailExamplesTab
                  key={context.topicTitle}
                  tenantId={tenantId}
                  context={context}
                  overviewFilters={overviewFilters}
                  globalFilters={globalFilters}
                  scrollContainerRef={examplesScrollRef}
                />
              )}
            </TabsContent>

            <TabsContent value="fatigue" className="mt-0 min-h-0 flex-1 overflow-y-auto">
              {activeTab === "fatigue" && (
                <TopicDetailFatigueTab
                  key={context.topicTitle}
                  tenantId={tenantId}
                  context={context}
                  overviewFilters={overviewFilters}
                  globalFilters={globalFilters}
                  kpiSeries={kpiSeries}
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
